import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MapPin, Link, MessageCircle, UserPlus, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProfileCardProps {
  id: string;
  username?: string;
  displayName?: string;
  bio?: string;
  profileImageUrl?: string;
  customLinks?: any[];
  score?: number;
  isDiscoverable?: boolean;
  className?: string;
  onInteract?: (action: 'follow' | 'message' | 'view', profileId: string) => void;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  id,
  username,
  displayName,
  bio,
  profileImageUrl,
  customLinks = [],
  score,
  isDiscoverable = true,
  className,
  onInteract
}) => {
  const name = displayName || username || 'Anonymous User';
  const hasLinks = customLinks && customLinks.length > 0;
  
  return (
    <Card className={cn(
      "group hover:shadow-lg transition-all duration-300 overflow-hidden",
      "border border-border/50 hover:border-primary/50",
      "bg-gradient-to-br from-card via-card to-muted/10",
      score && score > 0.8 && "ring-1 ring-primary/30",
      className
    )}>
      {/* Header */}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Avatar className="h-12 w-12 flex-shrink-0 ring-2 ring-background">
              <AvatarImage src={profileImageUrl} alt={name} />
              <AvatarFallback className="text-sm bg-gradient-to-br from-primary/20 to-accent/20">
                {name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base truncate">{name}</h3>
                <Badge variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20">
                  Profile
                </Badge>
              </div>
              
              {username && (
                <p className="text-sm text-muted-foreground">@{username}</p>
              )}
              
              {isDiscoverable && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span>Open to connect</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Relevance Score */}
          {score && score > 0.3 && (
            <div className="flex-shrink-0">
              <div className="text-xs text-primary font-mono bg-primary/10 px-2 py-1 rounded-full">
                {Math.round(score * 100)}% match
              </div>
            </div>
          )}
        </div>
      </CardHeader>

      {/* Content */}
      <CardContent className="pt-0 space-y-4">
        {/* Bio */}
        {bio && (
          <div>
            <p className="text-sm text-foreground/90 leading-relaxed line-clamp-3">
              {bio}
            </p>
          </div>
        )}

        {/* Custom Links Preview */}
        {hasLinks && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Link className="h-3 w-3" />
              <span>Links</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {customLinks.slice(0, 3).map((link, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="text-xs max-w-32 truncate"
                >
                  {link.title || link.url || 'Link'}
                </Badge>
              ))}
              {customLinks.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{customLinks.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Why this profile matches */}
        <div className="p-3 bg-muted/50 rounded-lg border border-border/30">
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Potential match:</span> This person's profile suggests they might be relevant to your search
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2 border-t border-border/50">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onInteract?.('follow', id)}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <UserPlus className="h-3 w-3" />
              Follow
            </button>
            
            <button
              onClick={() => onInteract?.('message', id)}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <MessageCircle className="h-3 w-3" />
              Message
            </button>
          </div>
          
          <button
            onClick={() => onInteract?.('view', id)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>View Profile</span>
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
};